import { HTMLAttributes, ReactNode, useMemo } from 'react';

import { VariantProps, cva } from 'class-variance-authority';
import { cn } from 'utils/helpers';
import { Avatar } from 'ui/components/img';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { DATE_TIME_FORMAT, SIZE } from 'utils/constants';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import { inflect } from 'inflection';
import { useTimezone } from 'utils/providers/timezone.provider';
import dayjs from 'dayjs';

import Status from '../Status';

const cell = cva(['text-title', 'break-all', 'text-sm', 'truncate'], {
  variants: {
    type: {
      status: ['flex flex-col justify-center'],
      icon: ['flex', 'gap-3', 'items-center', 'font-semibold'],
      email: [],
      avatar: [],
      label: [],
    },
    size: {
      sm: [],
      md: [],
    },
  },
  compoundVariants: [
    {
      type: 'icon',
      size: 'sm',
      className: ['py-5', 'px-4'],
    },
    {
      type: 'icon',
      size: 'md',
      className: ['py-6', 'px-4'],
    },
    {
      type: 'status',
      size: 'sm',
      className: ['py-5', 'px-4'],
    },
    {
      type: 'status',
      size: 'md',
      className: ['py-6', 'px-4'],
    },
    {
      type: 'label',
      size: 'sm',
      className: ['py-[18px] px-4'],
    },
    {
      type: 'label',
      size: 'md',
      className: ['py-[26px] px-4'],
    },
    {
      type: 'avatar',
      size: 'sm',
      className: ['py-[18px] px-4'],
    },
    {
      type: 'avatar',
      size: 'md',
      className: ['py-[26px] px-4'],
    },
  ],
  defaultVariants: {
    size: 'sm',
    type: 'label',
  },
});

export type CellProps = VariantProps<typeof cell> &
  HTMLAttributes<HTMLDivElement> & {
    data: {
      avatars?: AvatarOption[];
      avatarUnit?: string;
      icon?: any;
      label?: string | number | ReactNode;
      [key: string]: any;
    };
  };

const Cell: React.FC<CellProps> = ({
  type,
  data,
  className,
  size,
  ...props
}) => {
  const { icon: Icon, label, avatars, avatarUnit = 'user' } = data;
  const { timezone } = useTimezone({ source: 'account' });
  const isDateTime = typeof label === 'string' && dayjs(label).isValid();
  const typeMapper = useMemo(() => {
    switch (type) {
      case 'label': {
        return (
          <div>
            {isDateTime
              ? dayjs(label).tz(timezone).format(DATE_TIME_FORMAT)
              : label}
          </div>
        );
      }
      case 'avatar': {
        if (!avatars?.length) return null;
        if (avatars?.length === 1) {
          const avatar = avatars?.[0];
          return (
            <div className="flex gap-3 items-center">
              <Avatar size={SIZE.Small} user={avatar} />
              <div>{avatar.itemLabel}</div>
            </div>
          );
        }
        return (
          <div className="flex gap-3 items-center">
            <AvatarGroup size={SIZE.Small} users={avatars as AvatarOption[]} />
            <div>
              {avatars?.length}{' '}
              {inflect(avatarUnit as string, avatars?.length as number)}
            </div>
          </div>
        );
      }
      case 'email':
        return <></>;
      case 'icon':
        return (
          <>
            <Icon className="w-5 h-5" />
            {label}
          </>
        );
      case 'status':
        return (
          <>
            <div className="text-sm leading-[22px] tracking-[0.56px]">
              {data.label}
            </div>
            <Status type={data.status} />
          </>
        );
      default:
        return data.label;
    }
  }, [
    Icon,
    avatarUnit,
    avatars,
    data.label,
    data.status,
    label,
    type,
    isDateTime,
    timezone,
  ]);

  return (
    <div className={cn(cell({ type, className, size }))} {...props}>
      {typeMapper}
    </div>
  );
};

export default Cell;
